// ============ FEATURED GROUPS — กลุ่มหุ้นเด่นยุค AI ============
// Six AI-age theme groups straight from the upstream Featured Stocks research
// (served through our proxy at /api/nopoly?feed=featured), rendered in Master
// of the Market's own style with OUR live quotes (Finnhub via /api/quotes) and
// the FMP logo CDN. Upstream prices are a daily snapshot and are ignored.
//
// Every description is shown as published, in English, with a Thai translation
// underneath — the group names, the thesis and each company blurb. The Thai is
// keyed by group/ticker id, so a name we have not translated still renders its
// English original instead of a blank line.
//
// (An editorial six-theme version with its own Thai copy used to live here
// behind a source switch; see commit fc711e4~1 if it is ever wanted back.)
import React, { useEffect, useMemo, useState } from 'react';
import { SecHead } from './core';
import { fetchStockQuotes } from '../services/api';
import { useT } from '../lib/i18n';

const LOGO = 'https://images.financialmodelingprep.com/symbol/';

interface FeatStock { symbol: string; name: string; about?: string; }
interface FeatGroup { id: string; title: string; thesis?: string; stocks: FeatStock[]; news?: { headline: string }[]; }
interface Featured { asOf?: string; groups?: FeatGroup[]; }

const COLOR: Record<string, string> = {
  semiconductor: '#00e0b0', hyperscaler: '#37c8f0', nuclear: '#f0c75a',
  memory: '#a78bfa', software: '#ff8fa3', datacenter: '#7dd3fc',
};

const TH_GROUP: Record<string, { title: string; thesis: string }> = {
  semiconductor: {
    title: 'เซมิคอนดักเตอร์',
    thesis: 'โมเดล AI ทำงานบนซิลิคอน นี่คือทั้งกองที่ออกแบบและผลิตมัน — GPU และชิปเร่งความเร็วที่ใช้เทรนและรันโมเดล โรงงานที่ผลิตชิปเหล่านั้น และผู้ผูกขาดเครื่องพิมพ์ลายวงจรที่ทำให้ชิประดับล้ำสุดเกิดขึ้นได้',
  },
  hyperscaler: {
    title: 'ไฮเปอร์สเกลเลอร์',
    thesis: 'ยักษ์คลาวด์ที่แข่งกันสร้างดาต้าเซ็นเตอร์ AI แล้วปล่อยเช่ากำลังประมวลผลข้างใน งบลงทุนรวมกันปีละหลายแสนล้านดอลลาร์ คือเงินที่หล่อเลี้ยงห่วงโซ่ AI ทั้งสาย',
  },
  nuclear: {
    title: 'พลังงานนิวเคลียร์',
    thesis: 'ดาต้าเซ็นเตอร์ AI ต้องการไฟฟ้าที่จ่ายได้แน่นอน ไร้คาร์บอน ตลอด 24 ชม. ซึ่งตรงกับนิวเคลียร์พอดี เตาปฏิกรณ์โมดูลาร์ขนาดเล็ก (SMR) รุ่นใหม่กำลังเร่งเข้ามาอุดช่องว่างนี้ โดยมีรัฐบาลสหรัฐหนุนเต็มที่',
  },
  memory: {
    title: 'หน่วยความจำ & สตอเรจ',
    thesis: 'AI ไม่ได้ต้องการแค่กำลังประมวลผล แต่ต้องมีที่เก็บข้อมูลด้วย หน่วยความจำแบนด์วิดท์สูงต้องอยู่ข้าง GPU ทุกตัว และข้อมูลมหาศาลต้องถูกเก็บไว้ที่ไหนสักแห่ง ภาวะขาดแคลน DRAM และ NAND ที่ยืดเยื้อหลายปีทำให้กลุ่มนี้เป็นตัวเล่นที่ผูกกับการสร้าง AI แรงที่สุดกลุ่มหนึ่ง',
  },
  software: {
    title: 'ซอฟต์แวร์',
    thesis: 'ชั้นแอปพลิเคชันของ AI — แพลตฟอร์มและเครื่องมือที่แปลงโมเดลดิบให้กลายเป็นของที่องค์กรและภาครัฐใช้งานจริง ตั้งแต่ระบบปฏิบัติการข้อมูล เอเจนต์ AI อัตโนมัติ ไปจนถึงความปลอดภัยไซเบอร์',
  },
  datacenter: {
    title: 'โครงสร้างดาต้าเซ็นเตอร์',
    thesis: 'ชิปกับเงินทุนไม่กลายเป็น AI ได้เอง ต้องมีคนสร้างดาต้าเซ็นเตอร์ นี่คือกระดูกสันหลังทางกายภาพ: ตัวอาคาร ระบบไฟและระบายความร้อน เครือข่าย เซิร์ฟเวอร์ และ "นีโอคลาวด์" ที่เอาทั้งหมดนี้ไปปล่อยเช่า',
  },
};

const TH_ABOUT: Record<string, string> = {
  // Semiconductor
  NVDA: 'ออกแบบ GPU และระบบ full-rack ที่ขับเคลื่อนโมเดล AI แนวหน้าแทบทุกตัว ซอฟต์แวร์ CUDA และเครือข่าย NVLink ทำให้ลูกค้าย้ายค่ายได้ยาก',
  AMD: 'คู่แข่ง GPU หลักของ Nvidia ชิปเร่ง Instinct และซีพียูเซิร์ฟเวอร์ EPYC ทำให้ดาต้าเซ็นเตอร์มีทางเลือกที่สองสำหรับงาน AI',
  AVGO: 'สร้างชิป AI สั่งทำ (XPU) ให้ไฮเปอร์สเกลเลอร์อย่าง Google และ Meta พร้อมป้อนซิลิคอนอีเทอร์เน็ตที่เชื่อมคลัสเตอร์ AI ขนาดยักษ์',
  MRVL: 'ออกแบบชิปเร่ง AI สั่งทำให้ไฮเปอร์สเกลเลอร์ และผลิตชิปออปติคัล/เครือข่ายที่ขนข้อมูลภายในคลัสเตอร์ AI',
  TSM: 'ผู้รับจ้างผลิตชิปรายใหญ่ที่สุดในโลก โปรเซสเซอร์ AI ระดับสูงแทบทุกตัวถูกผลิตจริงในโรงงานของบริษัทนี้',
  ASML: 'ผู้ผลิตเครื่อง EUV lithography สัญชาติเนเธอร์แลนด์ที่จำเป็นต่อการพิมพ์ชิปล้ำสมัยที่สุด เป็นการผูกขาดจริงในระดับ leading edge',
  // Hyperscaler
  MSFT: 'ขาย AI สามทาง: คลาวด์ Azure, โมเดลแนวหน้าผ่าน OpenAI และ Copilot ในออฟฟิศ — AI ไหลผ่านแอปที่ธุรกิจจ่ายเงินใช้อยู่แล้ว',
  GOOGL: 'ทำ Google Cloud ออกแบบชิป TPU ของตัวเอง และสร้างโมเดล Gemini เป็นกอง AI ที่ครบวงจรตั้งแต่ซิลิคอนถึงซอฟต์แวร์',
  AMZN: 'ผู้ให้บริการ AWS แพลตฟอร์มคลาวด์ใหญ่ที่สุดในโลก ชิป Trainium และ Inferentia ของตัวเองช่วยลดการพึ่งพา Nvidia',
  META: 'หนึ่งในผู้สร้างโครงสร้าง AI ระดับไฮเปอร์สเกลรายใหญ่ที่สุด ดาต้าเซ็นเตอร์และชิปของตัวเองขับเคลื่อนโมเดล Llama และเครื่องยนต์โฆษณา',
  ORCL: 'Oracle Cloud Infrastructure กลายเป็นผู้ชนะสายคลาวด์ AI แบบเหนือความคาดหมาย เซ็นดีลหลายปีมูลค่ามหาศาลเพื่อรับงานเทรนโมเดล',
  CRWV: 'ผู้นำ "นีโอคลาวด์" — ไฮเปอร์สเกลเลอร์ AI ล้วน ๆ ปล่อยเช่ากำลัง GPU ของ Nvidia ในสเกลใหญ่ให้แล็บ AI และองค์กร',
  // Nuclear
  OKLO: 'สร้างเตาปฏิกรณ์เร็วขนาดกะทัดรัด Aurora ขายเป็นบริการพลังงานให้ดาต้าเซ็นเตอร์ Sam Altman พาเข้าตลาดปี 2024',
  SMR: 'ดีไซน์ SMR รายเดียวที่ผ่านการอนุมัติจากหน่วยงานกำกับนิวเคลียร์สหรัฐ โมดูล 77 MW ผลิตจากโรงงานแล้วนำมาต่อกันเป็นโรงไฟฟ้าเต็มรูปแบบ',
  CEG: 'เดินเครื่องฟลีตนิวเคลียร์ใหญ่ที่สุดในสหรัฐ เป็นพาร์ทเนอร์อันดับแรกเมื่อไฮเปอร์สเกลเลอร์ต้องการไฟฟ้าที่แน่นอนและไร้คาร์บอน',
  CCJ: 'หนึ่งในผู้ผลิตยูเรเนียมรายใหญ่ที่สุดของโลก และร่วมถือหุ้น Westinghouse เป็นตัวเล่นแบบ picks-and-shovels ของการสร้างเตาทุกรอบ',
  LEU: 'บริษัทเดียวที่เสริมสมรรถนะยูเรเนียมบนแผ่นดินสหรัฐ และเป็นผู้ผลิต HALEU รายแรกฝั่งตะวันตกที่ได้ใบอนุญาต — เชื้อเพลิงที่ SMR รุ่นใหม่ส่วนใหญ่ต้องใช้',
  VST: 'หนึ่งในผู้ผลิตไฟฟ้าอิสระรายใหญ่ที่สุดของสหรัฐ ผสมนิวเคลียร์กับก๊าซและแบตเตอรี่ และทำสัญญาขายตรงเข้าดาต้าเซ็นเตอร์มากขึ้นเรื่อย ๆ',
  // Memory & storage
  MU: 'ผู้ผลิตหน่วยความจำ DRAM และ NAND รายเดียวที่ฐานอยู่ในสหรัฐ เป็นซัพพลายเออร์หลักของ HBM ที่ต้องอยู่ข้างชิปเร่ง AI ทุกตัว',
  SNDK: 'ผู้ผลิต NAND flash ล้วน ๆ ที่แยกตัวออกจาก Western Digital ปี 2025 ป้อนแฟลชให้ SSD อุปกรณ์ต่าง ๆ และดาต้าเซ็นเตอร์ AI',
  WDC: 'กลายเป็นผู้นำฮาร์ดดิสก์ล้วนหลังปั่น SanDisk ออกไป ไดรฟ์ความจุสูงใช้เก็บข้อมูลดาต้าเซ็นเตอร์และข้อมูลเทรน AI',
  STX: 'ยักษ์ฮาร์ดดิสก์อีกราย เทคโนโลยี HAMR อัดความจุต่อไดรฟ์ได้มากขึ้นเรื่อย ๆ สำหรับสตอเรจระดับไฮเปอร์สเกล',
  SIMO: 'ออกแบบชิปคอนโทรลเลอร์ที่ทำให้ NAND flash ใช้งานได้จริงใน SSD และการ์ดหน่วยความจำ เป็นซัพพลายเออร์เบื้องหลังผู้ผลิตแฟลชทุกราย',
  NTAP: 'ขายระบบสตอเรจ all-flash สำหรับองค์กรและการจัดการข้อมูลแบบไฮบริดคลาวด์ เป็นชั้นระบบที่แปลงไดรฟ์ดิบให้กลายเป็นโครงสร้างพร้อมใช้กับ AI',
  // Software
  PLTR: 'ขายแพลตฟอร์มปฏิบัติการ AI — AIP, Foundry และ Gotham ที่เอาโมเดลภาษาขนาดใหญ่ไปทำงานจริงในภาครัฐและองค์กร Fortune 500',
  CRM: 'แพลตฟอร์มบริหารความสัมพันธ์ลูกค้า (CRM) ที่ใหญ่ที่สุด กำลังผลักดัน Agentforce เอเจนต์ AI อัตโนมัติภายในแอปองค์กรของตัวเอง',
  NOW: 'ทำแพลตฟอร์มเวิร์กโฟลว์ที่องค์กรใหญ่ใช้กับงาน IT, HR และปฏิบัติการ โดยวางชั้น AI ทับทุกโมดูล',
  SNOW: 'แพลตฟอร์มคลาวด์ที่องค์กรใช้เก็บและค้นข้อมูล ตอนนี้เพิ่มเครื่องมือ AI และวิเคราะห์ข้อมูลทับเข้าไป',
  CRWD: 'แพลตฟอร์มความปลอดภัยไซเบอร์แบบ cloud-native ชั้นนำ ใช้ AI สกัดภัยคุกคามทั้งฝั่งอุปกรณ์ คลาวด์ และตัวตนผู้ใช้',
  DDOG: 'แพลตฟอร์ม observability ที่คอยเฝ้าแอปและโครงสร้างบนคลาวด์ และกำลังกลายเป็นเครื่องมือหลักในการเฝ้าระบบ AI ที่ใช้งานจริง',
  // Data-center infrastructure
  VRT: 'ป้อนระบบไฟ ระบายความร้อน และระบบจัดการความร้อนที่ทำให้แร็ค AI ความหนาแน่นสูงทำงานได้ เป็นตัวเล่น picks-and-shovels ตรง ๆ ของการสร้างดาต้าเซ็นเตอร์',
  NBIS: 'นีโอคลาวด์ AI ที่แยกตัวออกมาจาก Yandex สร้างและปล่อยเช่ากำลัง GPU บนคลาวด์ให้นักพัฒนา AI',
  ETN: 'ยักษ์ด้านการจัดการพลังงาน ป้อนสวิตช์เกียร์และระบบไฟสำรองที่ส่งไฟเข้าดาต้าเซ็นเตอร์',
  ANET: 'สร้างสวิตช์อีเทอร์เน็ตความเร็วสูงที่เชื่อม GPU หลายพันตัวให้เป็นคลัสเตอร์ AI เดียว เป็นกระดูกสันหลังเครือข่ายของระบบเทรนขนาดใหญ่',
  SMCI: 'ประกอบเซิร์ฟเวอร์ AI และแร็คเต็มระบบ รวมถึงรุ่นระบายความร้อนด้วยของเหลว ส่งมอบซิลิคอนของ Nvidia และ AMD เข้าดาต้าเซ็นเตอร์ได้เร็ว',
  DLR: 'REIT ดาต้าเซ็นเตอร์ที่เป็นเจ้าของและปล่อยเช่าตัวอาคารจริง คืออสังหาฯ ที่กำลังประมวลผล AI ตั้งอยู่',
};

export function FeaturedGroups() {
  const t = useT();
  const [f, setF] = useState<Featured | null>(null);
  const [stale, setStale] = useState(false);
  const [failed, setFailed] = useState(false);
  const [gid, setGid] = useState('');
  const [quotes, setQuotes] = useState<Record<string, { price: number; chg: number }>>({});
  const [broken, setBroken] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let alive = true;
    fetch('/api/nopoly?feed=featured')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => { if (alive) { setF(v.data); setStale(!!v.stale); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  // Upstream JSON is stored as received (server/nopoly.mjs), so nothing in it is
  // promised: a group without a stocks list, or a feed without groups, must not
  // take the Scanner page down or leave this card on "Loading…" for ever.
  const groups = (Array.isArray(f?.groups) ? f!.groups : []).filter(g => g && g.id);
  const group = useMemo(() => groups.find(g => g.id === gid) || groups[0], [groups, gid]);

  useEffect(() => {
    let alive = true;
    const syms = (group?.stocks || []).map(s => s.symbol).filter(s => !(s in quotes));
    if (!syms.length) return;
    fetchStockQuotes(syms).then(q => { if (alive) setQuotes(x => ({ ...x, ...q })); });
    return () => { alive = false; };
  }, [group]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return null; // upstream down and no snapshot — drop the card rather than show an error
  if (f && !group) return null; // loaded, but nothing usable in it
  if (!group) {
    return (
      <div className="mm-card glass mm-featgrp">
        <SecHead th="กลุ่มหุ้นเด่นยุค AI" en="FEATURED GROUPS · AI-AGE THEMES"></SecHead>
        <p className="dim" style={{ padding: '8px 2px' }}>{t('กำลังโหลด…', 'Loading…')}</p>
      </div>
    );
  }

  const th = TH_GROUP[group.id];

  return (
    <div className="mm-card glass mm-featgrp" style={{ ['--cc' as any]: COLOR[group.id] || '#00e0b0' }}>
      <div className="mm-guru-head">
        <SecHead th="กลุ่มหุ้นเด่นยุค AI" en="FEATURED GROUPS · AI-AGE THEMES"></SecHead>
        <div className="mm-guru-tabs">
          {groups.map(g => {
            const c = COLOR[g.id] || '#00e0b0';
            const on = group.id === g.id;
            return (
              <button key={g.id} className={`mm-pill ${on ? 'on' : ''}`}
                style={on ? { borderColor: c, color: c, boxShadow: `0 0 14px ${c}44` } : {}}
                onClick={() => setGid(g.id)}>
                {/* English only — the Thai name doubled the pill height and wrapped */}
                {g.title}
              </button>
            );
          })}
        </div>
      </div>

      {group.thesis && (
        <div className="mm-featgrp-thesis">
          <p>{group.thesis}</p>
          {th && <p className="th">{th.thesis}</p>}
        </div>
      )}

      <div className="mm-featgrp-grid">
        {(Array.isArray(group.stocks) ? group.stocks : []).filter(s => s && s.symbol).map(s => {
          const q = quotes[s.symbol];
          return (
            <div className="mm-featgrp-stock" key={s.symbol}>
              {broken[s.symbol]
                ? <span className="mm-featgrp-logo mono">{s.symbol.slice(0, 4)}</span>
                : <img className="mm-featgrp-logo" src={`${LOGO}${s.symbol}.png`} alt={s.symbol} loading="lazy"
                    onError={() => setBroken(x => ({ ...x, [s.symbol]: true }))} />}
              <div className="mm-featgrp-body">
                <div className="mm-featgrp-h">
                  <b className="mono">{s.symbol}</b>
                  <span className="dim">{s.name}</span>
                  {q && <span className={`mono ${q.chg >= 0 ? 'up' : 'down'}`} style={{ marginLeft: 'auto' }}>
                    {q.price.toLocaleString(undefined, { maximumFractionDigits: 2 })} <small>{q.chg >= 0 ? '▲' : '▼'}{Math.abs(q.chg).toFixed(2)}%</small></span>}
                </div>
                {s.about && <p className="dim">{s.about}</p>}
                {TH_ABOUT[s.symbol] && <p className="dim mm-featgrp-th">{TH_ABOUT[s.symbol]}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {!!group.news?.length && (
        <div className="mm-featgrp-news">
          <span className="mm-std-lbl mono">{t('ข่าวประจำกลุ่ม', 'SECTOR NEWS')}</span>
          <ul>{group.news.map((n, i) => <li key={i}>{n.headline}</li>)}</ul>
        </div>
      )}

      <p className="mm-footnote dim">
        {t('การจัดกลุ่มโดย nopoly.ai', 'Grouping by nopoly.ai')}{f?.asOf ? ` · ${f.asOf}` : ''}
        {stale ? t(' · ข้อมูลสำรอง (ต้นทางไม่ตอบ)', ' · snapshot (upstream unreachable)') : ''}
        {' · '}{t('ราคาเรียลไทม์จาก Finnhub · คำแปลไทยโดยทีมงาน · เพื่อการศึกษา ไม่ใช่คำแนะนำการลงทุน',
          'live prices via Finnhub · Thai translation ours · education only, not investment advice')}
      </p>
    </div>
  );
}
